import { type FC, forwardRef } from "react";

import { INDEX_NOT_FOUND } from "components/constants";
import {
  INTERACTION_MODE_ICONS,
  PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_HIDE,
  PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_SHOW,
} from "components/materials/page/interaction-mode/helpers";
import { InteractionMode, PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { DualButton } from "mds/components/DualButton";
import { IconText } from "mds/components/IconText";
import { Tooltip } from "mds/components/Tooltip";
import { ChevronDownIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectHasSubmissionsForPageId } from "store/selectors";

interface GroupButtonProps {
  title: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  page: PageType;
  text: string;
}

const GroupButton: FC<GroupButtonProps> = ({ title, onClick, page, text }) => {
  return (
    <Button
      disabled={!onClick}
      eventName="Group Settings - Toggle"
      kind="chipHibiscus"
      size="xs"
      title={title}
      onClick={onClick}
    >
      <IconText
        className="gap-1.5 text-xs"
        containerQueryClassNameHide={PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_HIDE}
        containerQueryClassNameShow={PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_SHOW}
        iconStart={INTERACTION_MODE_ICONS[page.interaction_mode]}
        text={text}
      />
    </Button>
  );
};

interface GroupMenuButtonProps {
  page: PageType;
  onGroupConfigCardToggle?: (interactionMode: InteractionMode) => void;
  onStudentGroupCardToggle?: () => void;
  pageGroupIndex: number;
  /** When the GroupMenuButton is restricted, this means that instructors:
   *   - will not see the dual group menu button (they may not modify the page's interaction mode).
   *   - will be able to click on the group button to see either 1) the student group card or 2) the group config card.
   *   - will only be allowed the following actions on the group config card: 1) drag and drop individual students
   *   or 2) create another page group. */
  restricted: boolean;
  onClick?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
}

export const GroupMenuButton = forwardRef<HTMLDivElement, GroupMenuButtonProps>(
  (
    {
      page,
      onGroupConfigCardToggle,
      onStudentGroupCardToggle,
      pageGroupIndex,
      restricted,
      onClick,
      onKeyDown,
    },
    ref,
  ) => {
    const hasSubmissions = useAppSelector((s) => selectHasSubmissionsForPageId(s, page.id));
    const shouldOpenStudentGroupCard =
      pageGroupIndex !== INDEX_NOT_FOUND && onStudentGroupCardToggle;

    if (page.interaction_mode === InteractionMode.STATIC) {
      return null;
    }

    const buttonTitle = shouldOpenStudentGroupCard
      ? t("common.view_group", {
          number: pageGroupIndex + 1,
        })
      : t("tooltip.configure_groups");

    const buttonText = shouldOpenStudentGroupCard
      ? t("common.group_title", {
          number: pageGroupIndex + 1,
        })
      : t(`page_interaction_mode_menu.interaction_mode.${page.interaction_mode}`);

    const onGroupButtonClick =
      !shouldOpenStudentGroupCard && !onGroupConfigCardToggle
        ? undefined
        : (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            e.stopPropagation();
            e.preventDefault();
            if (shouldOpenStudentGroupCard) {
              onStudentGroupCardToggle();
            } else {
              onGroupConfigCardToggle?.(page.interaction_mode);
            }
          };

    const groupButton = (
      <GroupButton
        page={page}
        text={buttonText}
        title={!restricted ? buttonTitle : ""}
        onClick={onGroupButtonClick}
      />
    );

    if (restricted) {
      if (hasSubmissions || page.submissions_last_collected_at) {
        return <Tooltip text={t("tooltip.group_config_has_submissions")}>{groupButton}</Tooltip>;
      }

      return groupButton;
    }

    return (
      // If the page has a group interaction mode, then we want to show a DualButton, the left half
      // of which just re-opens the menu without displaying the dropdown, and the right half of which
      // displays a dropdown which then allows changing of interaction mode.
      <DualButton
        buttonLeft={groupButton}
        buttonRight={
          <Button
            className="!px-2"
            kind="chipHibiscus"
            size="xs"
            title={t("tooltip.change_type_of_activity")}
          >
            <ChevronDownIcon size="xs" />
          </Button>
        }
        ref={ref}
        onClick={onClick}
        onKeyDown={onKeyDown}
      />
    );
  },
);

GroupMenuButton.displayName = "GroupMenuButton";
